import React, { useEffect, useState, useCallback } from 'react';
import { Zap, ChevronDown, ChevronUp, Shield, Wrench, RefreshCw } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation, formatCount } from '@/i18n';
import { apiClient, ToolAnalyticsResponse } from '@/utils/apiClient';

interface ToolAnalyticsPanelProps {
  className?: string;
}

export const ToolAnalyticsPanel: React.FC<ToolAnalyticsPanelProps> = ({ className }) => {
  const { t, language } = useTranslation();
  const counts = t.analytics.counts;
  const at = (t as any).analytics;
  const [data, setData] = useState<ToolAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getToolAnalytics(30);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : (at?.fetchError ?? 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, [at?.fetchError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
          <RefreshCw className="h-4 w-4 animate-spin" />
          <span className="text-sm">{at?.loading ?? 'Loading...'}</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4', className)}>
        <div className="flex items-center justify-between">
          <span className="text-sm text-red-500 dark:text-red-400">{error}</span>
          <button onClick={fetchData} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {at?.refresh ?? 'Retry'}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const efficiencyColor = data.efficiency_percent >= 80
    ? 'text-green-600 dark:text-green-400'
    : data.efficiency_percent >= 50
    ? 'text-yellow-600 dark:text-yellow-400'
    : 'text-red-600 dark:text-red-400';

  const efficiencyBg = data.efficiency_percent >= 80
    ? 'bg-green-500'
    : data.efficiency_percent >= 50
    ? 'bg-yellow-500'
    : 'bg-red-500';

  return (
    <div className={clsx('bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700', className)}>
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <Zap className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {at?.toolCalls ?? 'Tool Calls'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {formatCount(language, data.total_tool_calls, counts.toolCalls, data.total_tool_calls.toLocaleString())} · {formatCount(language, data.unique_tools, counts.uniqueTools)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <span className={clsx('text-lg font-bold tabular-nums', efficiencyColor)}>
              {data.efficiency_percent.toFixed(0)}%
            </span>
            <p className="text-[10px] text-gray-400">{at?.efficiency ?? 'Efficiency'}</p>
          </div>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 dark:border-gray-700 pt-4">
          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
              <Wrench className="h-4 w-4 mx-auto text-blue-500 mb-1" />
              <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{data.total_tool_calls}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">{at?.totalCalls ?? 'Total Calls'}</p>
            </div>
            <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
              <Shield className="h-4 w-4 mx-auto text-green-500 mb-1" />
              <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{data.blocked_duplicates}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">{at?.blockedDuplicates ?? 'Blocked'}</p>
            </div>
            <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
              <Zap className="h-4 w-4 mx-auto text-purple-500 mb-1" />
              <p className="text-lg font-bold text-gray-900 dark:text-white tabular-nums">{data.unique_tools}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">{at?.uniqueTools ?? 'Unique'}</p>
            </div>
          </div>

          {/* Efficiency bar */}
          <div>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-500 dark:text-gray-400">{at?.efficiency ?? 'Efficiency'}</span>
              <span className={clsx('font-medium', efficiencyColor)}>
                {data.efficiency_percent.toFixed(1)}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={clsx('h-full rounded-full transition-all duration-500', efficiencyBg)}
                style={{ width: `${Math.min(data.efficiency_percent, 100)}%` }}
              />
            </div>
          </div>

          {/* Top tools */}
          {data.top_tools.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {at?.topTools ?? 'Top Tools'}
              </h4>
              <ul className="space-y-1.5">
                {data.top_tools.slice(0, 5).map((tool) => {
                  const maxCount = data.top_tools[0]?.count || 1;
                  return (
                    <li key={tool.name} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-mono text-gray-700 dark:text-gray-300 truncate">
                            {tool.name}
                          </span>
                          <span className="text-gray-400 dark:text-gray-500 tabular-nums ml-2 flex-shrink-0">
                            {tool.count}× · {tool.avg_duration_ms.toFixed(0)} {at?.ms ?? 'ms'}
                          </span>
                        </div>
                        <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden mt-0.5">
                          <div
                            className="h-full bg-blue-400 dark:bg-blue-500 rounded-full"
                            style={{ width: `${(tool.count / maxCount) * 100}%` }}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Mini sparkline for calls by day */}
          {data.calls_by_day.length > 1 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                {at?.callsPerDay ?? 'Calls per Day'}
              </h4>
              <div className="flex items-end gap-px h-12">
                {data.calls_by_day.slice(-14).map((day, i) => {
                  const maxDay = Math.max(...data.calls_by_day.map(d => d.count), 1);
                  const height = Math.max((day.count / maxDay) * 100, 4);
                  return (
                    <div
                      key={i}
                      className="flex-1 bg-purple-400 dark:bg-purple-500 rounded-t-sm transition-all hover:bg-purple-500 dark:hover:bg-purple-400"
                      style={{ height: `${height}%` }}
                      title={`${day.date}: ${day.count}`}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
